import { useCallback, useEffect, useState } from 'react';
import { Users, Lock, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewerUsersTab({ events, adminToken }) {
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchViewerUsers = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/viewer/users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao buscar usuários viewer:', error);
      toast({ title: 'Erro', description: 'Não foi possível carregar os usuários de acompanhamento.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [adminToken, toast]);

  useEffect(() => {
    if (adminToken) {
      fetchViewerUsers();
    }
  }, [adminToken, fetchViewerUsers]);

  const toggleEventSelection = (eventId) => {
    setSelectedEventIds((current) =>
      current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId]
    );
  };

  const handleCreateViewer = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast({ title: 'Atenção', description: 'Preencha usuário e senha.', variant: 'destructive' });
      return;
    }

    try {
      await axios.post(`${API}/viewer/users`, {
        username,
        password,
        authorized_event_ids: selectedEventIds,
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      toast({ title: 'Sucesso', description: 'Usuário de acompanhamento criado com sucesso.' });
      setUsername('');
      setPassword('');
      setSelectedEventIds([]);
      fetchViewerUsers();
    } catch (error) {
      console.error('Erro ao criar usuário viewer:', error);
      toast({ title: 'Erro', description: error.response?.data?.detail || 'Não foi possível criar o usuário.', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase text-primary">Usuários de Acompanhamento</h2>
          <p className="text-sm text-foreground/70">Crie usuários e vincule quais eventos eles poderão ver.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-foreground/70">
          <Users className="w-5 h-5" />
          Acesso apenas leitura às inscrições dos eventos autorizados.
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-bold text-primary mb-4">Criar novo usuário</h3>
          <form onSubmit={handleCreateViewer} className="space-y-4">
            <div>
              <Label htmlFor="viewer-username" className="text-sm font-bold uppercase tracking-widest text-primary mb-2 block">Usuário</Label>
              <Input
                id="viewer-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Nome do usuário"
                className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4"
              />
            </div>
            <div>
              <Label htmlFor="viewer-password" className="text-sm font-bold uppercase tracking-widest text-primary mb-2 block">Senha</Label>
              <Input
                id="viewer-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Senha segura"
                className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4"
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2 block">Eventos autorizados</Label>
              <div className="grid gap-2 max-h-56 overflow-y-auto border border-border rounded-lg p-3 bg-background">
                {events.length > 0 ? (
                  events.map((event) => (
                    <label key={event.id} className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-primary/5">
                      <input
                        type="checkbox"
                        checked={selectedEventIds.includes(event.id)}
                        onChange={() => toggleEventSelection(event.id)}
                        className="h-4 w-4 border-border text-primary"
                      />
                      <span className="text-sm text-foreground">{event.name} ({event.date?.slice(0, 10)})</span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-foreground/70">Nenhum evento disponível para vincular.</p>
                )}
              </div>
            </div>
            <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm">
              <Lock className="w-4 h-4 mr-2" /> Criar usuário
            </Button>
          </form>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold uppercase text-primary">Usuários cadastrados</h3>
          </div>
          {loading ? (
            <p className="text-sm text-foreground/70">Carregando usuários...</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-foreground/70">Nenhum usuário de acompanhamento cadastrado.</p>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="border border-border rounded-lg p-4 bg-background">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-primary">{user.username}</p>
                      <p className="text-xs text-foreground/70">Ativo: {user.is_active ? 'Sim' : 'Não'}</p>
                    </div>
                    <span className="text-xs text-foreground/70">{user.authorized_event_ids?.length || 0} eventos</span>
                  </div>
                  {user.authorized_event_ids?.length > 0 && (
                    <div className="mt-3 text-sm text-foreground/80">
                      <strong className="block mb-1">Eventos autorizados:</strong>
                      <ul className="list-disc list-inside space-y-1">
                        {user.authorized_event_ids.map((eventId) => (
                          <li key={eventId}>{eventId}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
